import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconGalleryArrowLeft from "../../../../img/icons/gallery-arrow-left.svg";
import iconGalleryArrowRight from "../../../../img/icons/gallery-arrow-right.svg";
import { safeDestroy } from "../../../hooks/useFlickity";
import { isoReviewsProductID } from "../../../models/nominals";
import { IReview } from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { parseFacetValues } from "../../../utils/reviews";
import { CarouselReview } from "../CarouselReview";
import { ReviewsPageLink } from "../ReviewsPageLink";
import { defaultState } from "../defaults";
import { clearFilters } from "../reducers";

// Not using useFlickity hook: requires resize handling, pagination, and Redux-driven reinit
type FlickityConstructor = new (el: HTMLElement, options: object) => Flickity;

type IProps = Immutable<{
    showSideDate: boolean;
    enablePDPLinks: boolean;
    onLoadMore: () => void;
    maxKeywordsNumber: number;
    starHasStroke?: boolean;
}>;

const SMALL_SCREEN_THRESHOLD = 501;
const MEDIUM_SCREEN_THRESHOLD = 769;
const SMALL_SCREEN_TILES_LENGTH = 1;
const MEDIUM_SCREEN_TILES_LENGTH = 2;
const LARGE_SCREEN_TILES_LENGTH = 3;
const INITIAL_REVIEW_LIST_LENGTH = 6;
const ELEMENT_SCROLLABLE_THRESHOLD = 160;

const buildReviewKey = (review: IReview): string =>
    `${isoReviewsProductID.unwrap(review.product_id)}-${review.id}`;

const getDisplayTilesNumber = (): number => {
    const window = getWindow();
    if (!window) {
        return LARGE_SCREEN_TILES_LENGTH;
    }
    if (window.innerWidth >= MEDIUM_SCREEN_THRESHOLD) {
        return LARGE_SCREEN_TILES_LENGTH;
    }
    if (window.innerWidth >= SMALL_SCREEN_THRESHOLD) {
        return MEDIUM_SCREEN_TILES_LENGTH;
    }
    return SMALL_SCREEN_TILES_LENGTH;
};

const disableButton = (target: HTMLButtonElement): void => {
    target.setAttribute("disabled", "disabled");
    target.classList.add("disabled");
};

const enableButton = (target: HTMLButtonElement): void => {
    target.removeAttribute("disabled");
    target.classList.remove("disabled");
};

const updateTabIndices = (
    displayTilesNumber: number,
    elementScrollableThreshold: number,
): void => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const flickitySlider = document.querySelector(".flickity-slider");
    if (!flickitySlider) {
        return;
    }

    const items = flickitySlider.children;
    Array.from(items, (item) => {
        const reviewCopyContainer = item.children[0].children[1];
        const reviewCopy = reviewCopyContainer.children[1] as HTMLElement;
        if (item.classList.contains("is-selected")) {
            if (
                displayTilesNumber >= LARGE_SCREEN_TILES_LENGTH &&
                reviewCopy.offsetHeight >= elementScrollableThreshold
            ) {
                reviewCopyContainer.setAttribute("tabIndex", "0");
            }
        } else {
            reviewCopyContainer.setAttribute("tabIndex", "-1");
        }
    });
};

export const CarouselReviewList = (props: IProps): React.ReactNode => {
    const reviews = useAppSelector(
        (rootState) => (rootState.reviews || defaultState).data.reviews,
    );
    const hasMore = useAppSelector(
        (rootState) => (rootState.reviews || defaultState).ui.hasMore,
    );
    const keywords = useAppSelector(
        (rootState) =>
            `${(rootState.reviews || defaultState).ui.loadedFacetValues.text}`,
    );

    const dispatch = useAppDispatch();

    const [displayTilesNumber, setDisplayTilesNumber] = React.useState(
        LARGE_SCREEN_TILES_LENGTH,
    );
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [closeAllReviewColumns, setCloseAllReviewColumns] =
        React.useState(true);

    const previousButtonRef = React.useRef<HTMLButtonElement>(null);
    const nextButtonRef = React.useRef<HTMLButtonElement>(null);
    const currentReviewElementRef = React.useRef<HTMLButtonElement>(null);
    const reviewsCarouselRef = React.useRef<HTMLDivElement>(null);
    const carouselFlktyRef = React.useRef<Flickity | null>(null);
    const flickityClassRef = React.useRef<FlickityConstructor | null>(null);
    const previousReviewsRef = React.useRef<ReadonlyArray<IReview>>(reviews);

    const expandReviewColumn = (isExpanded: boolean): void => {
        setCloseAllReviewColumns(isExpanded);
    };

    const onNavigationClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ): void => {
        const direction = event.currentTarget.dataset.direction;
        if (carouselFlktyRef.current) {
            switch (direction) {
                case "previous":
                    carouselFlktyRef.current.previous();
                    break;
                case "next":
                    carouselFlktyRef.current.next();
                    break;
            }
        }
    };

    const initializeBaseCarousel = (): void => {
        if (carouselFlktyRef.current) {
            safeDestroy(carouselFlktyRef.current);
            carouselFlktyRef.current = null;
        }
        if (!reviewsCarouselRef.current || !flickityClassRef.current) {
            return;
        }

        const FlickityClass = flickityClassRef.current;
        carouselFlktyRef.current = new FlickityClass(
            reviewsCarouselRef.current,
            {
                cellAlign: "left",
                groupCells: displayTilesNumber,
                pageDots: false,
                prevNextButtons: false,
                initialIndex: currentIndex,
                selectedAttraction: 0.4,
                friction: 0.9,
                on: {
                    ready: (): void => {
                        reviewsCarouselRef.current?.removeAttribute("tabindex");
                    },
                },
            },
        );

        if (currentIndex === 0 && previousButtonRef.current) {
            disableButton(previousButtonRef.current);
        }

        carouselFlktyRef.current.on("select", (): void => {
            const carousel = carouselFlktyRef.current;
            if (!carousel) {
                return;
            }

            if (!carousel.cells[carousel.selectedIndex - 1]) {
                if (previousButtonRef.current) {
                    disableButton(previousButtonRef.current);
                }
            } else if (!carousel.cells[carousel.selectedIndex + 1]) {
                if (nextButtonRef.current) {
                    disableButton(nextButtonRef.current);
                }
            } else {
                if (previousButtonRef.current) {
                    enableButton(previousButtonRef.current);
                }
                if (nextButtonRef.current) {
                    enableButton(nextButtonRef.current);
                }
            }
        });

        carouselFlktyRef.current.on("settle", (index): void => {
            const idx = Number(index);
            if (hasMore && idx > currentIndex) {
                if (
                    (idx + 1) %
                        (INITIAL_REVIEW_LIST_LENGTH / displayTilesNumber) ===
                    0
                ) {
                    props.onLoadMore();
                }
            }
            setCurrentIndex(idx);
            setCloseAllReviewColumns(true);

            updateTabIndices(displayTilesNumber, ELEMENT_SCROLLABLE_THRESHOLD);
        });
    };

    // Handle screen resize
    React.useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }
        const checkScreenSize = (): void => {
            const newDisplayTilesNumber = getDisplayTilesNumber();
            if (newDisplayTilesNumber !== displayTilesNumber) {
                setDisplayTilesNumber(newDisplayTilesNumber);
                setCurrentIndex(0);
            }
        };

        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);
        return (): void => {
            window.removeEventListener("resize", checkScreenSize);
        };
    }, [displayTilesNumber]);

    // Handle reviews changes (replaces getSnapshotBeforeUpdate)
    React.useEffect(() => {
        if (previousReviewsRef.current !== reviews) {
            if (carouselFlktyRef.current) {
                safeDestroy(carouselFlktyRef.current);
                carouselFlktyRef.current = null;
            }

            const document = getDocument();
            if (document) {
                const reviewList = document.querySelectorAll(
                    ".customer-review__review-item",
                );
                if (reviewList.length === INITIAL_REVIEW_LIST_LENGTH) {
                    setCurrentIndex(0);
                }
            }

            previousReviewsRef.current = reviews;
        }
    }, [reviews]);

    // Initialize carousel
    React.useEffect(() => {
        if (reviews.length === 0) {
            return;
        }

        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        if (flickityClassRef.current) {
            initializeBaseCarousel();
        } else {
            import("flickity-imagesloaded").then((module) => {
                flickityClassRef.current =
                    module.default as unknown as FlickityConstructor;
                initializeBaseCarousel();
            });
        }

        return (): void => {
            if (carouselFlktyRef.current) {
                safeDestroy(carouselFlktyRef.current);
                carouselFlktyRef.current = null;
            }
        };
    }, [reviews, displayTilesNumber]);

    if (reviews.length === 0) {
        return (
            <div className="customer-review__no-reviews">
                <p>
                    {t`Sorry, we could not find any reviews within your criteria.`}
                </p>
                <button onClick={() => dispatch(clearFilters())}>
                    {t`Clear filters and try again?`}
                </button>
            </div>
        );
    }

    const parsedKeywords =
        keywords && keywords.length > 0 ? parseFacetValues(keywords) : [];

    if (parsedKeywords.length > props.maxKeywordsNumber) {
        return (
            <div className="customer-review__no-reviews">
                <p>{t`Error: Too Many Filters Applied.`}</p>
                <button onClick={() => dispatch(clearFilters())}>
                    {t`Clear filters and try again`}
                </button>
            </div>
        );
    }

    const reviewsKey = reviews.map(buildReviewKey).join("|");

    return (
        <>
            <div
                className="customer-review__carousel-container"
                key={reviewsKey}
            >
                <div className="customer-review__carousel-buttons">
                    <button
                        ref={previousButtonRef}
                        aria-label={t`Previous`}
                        className="disabled"
                        data-direction="previous"
                        onClick={onNavigationClick}
                    >
                        <SVG aria-hidden="true" src={iconGalleryArrowLeft} />
                    </button>
                </div>
                <div
                    className="customer-review__carousel-review-list"
                    role="region"
                    aria-label={t`Customer Reviews Carousel`}
                    ref={reviewsCarouselRef}
                >
                    {reviews.map((data) => (
                        <CarouselReview
                            key={buildReviewKey(data)}
                            review={data}
                            showSideDate={props.showSideDate}
                            enablePDPLinks={props.enablePDPLinks}
                            isMobile={
                                displayTilesNumber < LARGE_SCREEN_TILES_LENGTH
                            }
                            starHasStroke={props.starHasStroke}
                            closeAllReviewColumns={closeAllReviewColumns}
                            expandReviewColumn={expandReviewColumn}
                            currentReviewElement={currentReviewElementRef}
                            expandContentThreshold={
                                ELEMENT_SCROLLABLE_THRESHOLD
                            }
                        />
                    ))}
                </div>
                <div className="customer-review__carousel-buttons">
                    <button
                        ref={nextButtonRef}
                        aria-label={t`Next`}
                        data-direction="next"
                        onClick={onNavigationClick}
                    >
                        <SVG aria-hidden="true" src={iconGalleryArrowRight} />
                    </button>
                </div>
            </div>
            <ReviewsPageLink className="button">
                {t`READ ALL REVIEWS`}
            </ReviewsPageLink>
        </>
    );
};
